import type { NotificationType, Sport } from '@h2h/shared';
import { notificationCtaLabel as sharedNotificationCtaLabel } from '@h2h/shared';

/** The deep-link payload carried on a notification (Firestore feed or FCM data). */
export interface NotificationRouteData {
  leagueId?: string;
  roundId?: string;
  duelId?: string;
  fixtureId?: string;
  miniLeagueId?: string;
  actorId?: string;
  /** Explicit in-app destination (manual ANNOUNCEMENT broadcasts). Wins over all
   *  type-specific routing so an admin push can land on any screen. */
  path?: string;
  /** Optional CTA label carried on a broadcast for the feed card. */
  ctaLabel?: string;
}

function qs(params: Record<string, string | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) p.set(k, v);
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** FCM `data` blobs arrive as strings — pull the fields the router cares about. */
export function parseNotificationRouteData(raw: unknown): { type: string; data: NotificationRouteData } {
  const d = (raw ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (v == null || v === '' ? undefined : String(v));
  return {
    type: str(d.type) ?? '',
    data: {
      leagueId: str(d.leagueId),
      roundId: str(d.roundId),
      duelId: str(d.duelId),
      fixtureId: str(d.fixtureId),
      miniLeagueId: str(d.miniLeagueId),
      actorId: str(d.actorId),
      path: str(d.path),
      ctaLabel: str(d.ctaLabel)
    }
  };
}

/**
 * Resolve the in-app route for a notification. Shared by the in-app feed and push
 * taps so both land on the same screen. Type-aware: a card reminder opens the pick
 * flow, a live goal opens the fixture card, settlement opens the round recap, etc.
 */
export function notificationRoute(
  type: string,
  data: NotificationRouteData,
  fallbackLeagueId?: string
): string | null {
  const leagueId = data.leagueId ?? fallbackLeagueId;
  const { roundId, duelId, fixtureId, miniLeagueId } = data;

  if (data.path && data.path.startsWith('/')) return data.path;

  if (duelId) return `/duel/${duelId}`;

  if (
    leagueId &&
    (type === 'NEW_ROUND' || type === 'PICKS_LOCK_SOON' || type === 'MINI_LEAGUE_REMINDER')
  ) {
    return `/play/${leagueId}${qs({ round: roundId })}`;
  }

  if (miniLeagueId && (type === 'MINI_LEAGUE_JOIN' || type === 'MINI_LEAGUE_RESULT')) {
    return `/leagues/${miniLeagueId}`;
  }

  if (
    leagueId &&
    fixtureId &&
    (type === 'MATCH_KICKOFF' || type === 'LIVE_GOAL' || type === 'MATCH_RESULT')
  ) {
    return `/play/${leagueId}/fixture/${fixtureId}${qs({ round: roundId })}`;
  }

  if (
    leagueId &&
    (type === 'ROUND_SETTLED' ||
      type === 'PROMOTED' ||
      type === 'RELEGATED' ||
      type === 'EXACT_BONUS' ||
      type === 'SCORER_BONUS')
  ) {
    return `/play/${leagueId}/results${qs({ round: roundId })}`;
  }

  if (leagueId && type === 'FRIEND_PASSED') {
    return `/play/${leagueId}/standings`;
  }

  if (miniLeagueId) return `/leagues/${miniLeagueId}`;
  if (leagueId && fixtureId) return `/play/${leagueId}/fixture/${fixtureId}${qs({ round: roundId })}`;
  if (leagueId && roundId) return `/play/${leagueId}/results${qs({ round: roundId })}`;
  if (leagueId) return `/play/${leagueId}`;
  if (fallbackLeagueId) return `/play/${fallbackLeagueId}`;
  return null;
}

/** When routing fails, fall back to the league's notification inbox. */
export function notificationRouteOrInbox(
  type: string,
  data: NotificationRouteData,
  fallbackLeagueId?: string
): string {
  const route = notificationRoute(type, data, fallbackLeagueId);
  if (route) return route;
  const leagueId = data.leagueId ?? fallbackLeagueId;
  if (leagueId) return `/play/${leagueId}/notifications`;
  return '/football';
}

/** Short CTA label for actionable notification cards (sport-aware). */
export function notificationCtaLabel(type: NotificationType, sport?: Sport | null): string | null {
  return sharedNotificationCtaLabel(type, sport);
}
